import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import {
  DomSanitizer,
  SafeResourceUrl,
  Title,
} from '@angular/platform-browser';
import { ActivatedRoute, Params, Router, RouterModule } from '@angular/router';
import { GameHistoryUiHistoryListTableModule } from '@fantaz/game-history/ui/history-list-table';
import { AuthFacade } from '@fantaz/shared/auth';
import {
  FzGoogleTagManagerService,
  MatchMakingService,
} from '@fantaz/shared/data-access/api';
import {
  CasualTournamentDetails,
  EventSchedule,
  GAEventKeys,
  NextEvent,
  RoomFilter,
  RoomFilterModel,
  SchedulesRecordItem,
} from '@fantaz/shared/data-access/models';
import { BreadcrumbsService } from '@fantaz/shared/layout/breadcrumbs';
import { ShareService } from '@fantaz/shared/share';
import { SharedUiConditionalModule } from '@fantaz/shared/ui/conditional';
import { EmptyListModule } from '@fantaz/shared/ui/empty-list';
import { SharedUiLoadingComponentModule } from '@fantaz/shared/ui/loading-component';
import { MaterialModule } from '@fantaz/shared/ui/material';
import { ModalAlertComponent } from '@fantaz/shared/ui/modal-alert';
import { SharedUiRouterTabsModule } from '@fantaz/shared/ui/router-tabs';
import { SkeletonModule } from '@fantaz/shared/ui/skeleton';
import { SharedUiTableComponentModule } from '@fantaz/shared/ui/table-component';
import { BlockDeviceService } from '@fantaz/shared/utils/block-device';
import { TournamentsUiFilterModule } from '@fantaz/tournaments-v2/ui/filters';
import {
  getExperience,
  multiNextEventsQuery,
  tournamentDetailsByUrlQuery,
} from '@fantaz/tournaments/data-access';
import { TournamentsUiMatchMakingDialogModule } from '@fantaz/tournaments/ui/match-making-dialog';
import { PageTabRoute } from '@fantaz/tournaments/ui/page-tab-route';
import { StatBoxComponent } from '@fantaz/tournaments/ui/stat-box';
import { TournamentsUiRoomsTableViewModule } from '@fantaz/tournaments/ui/table-view';
import { TournamentBannerModule } from '@fantaz/tournaments/ui/tournament-banner';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { QueryClient } from '@tanstack/angular-query-experimental';
import { MyHistoryTabComponent } from './my-history-tab/my-history-tab.component';

@UntilDestroy()
@Component({
  standalone: true,
  imports: [
    CommonModule,
    EmptyListModule,
    GameHistoryUiHistoryListTableModule,
    MaterialModule,
    MyHistoryTabComponent,
    RouterModule,
    SharedUiConditionalModule,
    SharedUiLoadingComponentModule,
    SharedUiRouterTabsModule,
    SharedUiTableComponentModule,
    SkeletonModule,
    StatBoxComponent,
    TournamentBannerModule,
    TournamentsUiFilterModule,
    TournamentsUiMatchMakingDialogModule,
    TournamentsUiRoomsTableViewModule,
  ],
  templateUrl: './tournament-events.html',
  styleUrls: ['./tournament-events.scss'],
})
export class TournamentEvents implements OnInit {
  #authFacade = inject(AuthFacade);
  #blockDeviceService = inject(BlockDeviceService);
  #title = inject(Title);
  #shareService = inject(ShareService);
  #route = inject(ActivatedRoute);
  #dialog = inject(MatDialog);
  #gtmService = inject(FzGoogleTagManagerService);
  #matchMakingService = inject(MatchMakingService);
  #sanitizer = inject(DomSanitizer);
  #breadcrumbService = inject(BreadcrumbsService);
  #router = inject(Router);
  #queryClient = inject(QueryClient);

  freeGameUrl: SafeResourceUrl | undefined;
  showFilter = false;
  schedulesFiltered: EventSchedule[] = [];

  view$ = this.#route.queryParams.pipe(untilDestroyed(this));
  filterModel: RoomFilterModel[] = [
    {
      filter: 'entryAmount',
      label: 'Entry Fee',
      symbol: '$',
    },
    {
      filter: 'prize',
      label: 'Prize',
      symbol: '$',
    },
    {
      filter: 'totalEntryCount',
      label: 'Seats',
      symbol: '',
    },
    {
      filter: 'status',
      label: 'Status',
      static: true,
      values: [
        {
          key: 'activeTournaments',
          value: {
            start: 0,
            end: 0,
          },
          label: 'Active',
          selected: true,
        },
        {
          key: 'retiredEvents',
          value: {
            start: 0,
            end: 0,
          },
          label: 'Completed',
          selected: false,
        },
      ],
    },
  ];

  firstEventSchedule: EventSchedule;
  tabsLinks: PageTabRoute[] = [
    {
      path: '',
      name: 'Tournaments',
      view: 'tournaments',
    },
    {
      path: '',
      name: 'My History',
      view: 'history',
    },
  ];
  view: string;
  tournamentType: string;

  // redesign
  isUserLoggedIn = toSignal(this.#authFacade.isLoggedIn$);
  currentUser = toSignal(this.#authFacade.currentUser$);

  /**
   * Tournament url parameter from the route. Using alias to match with the same
   * parameter name from the router configuration.
   */
  url = input<string>('', { alias: 'tournamentId' });

  /**
   * Query configuration for fetching a tournament details.
   */
  details = tournamentDetailsByUrlQuery({
    url: this.url,
  });

  /**
   * A computed property that returns an array of events for the event schedule.
   * It maps over the `detailsForEventSchedule` array from the `details` data and creates
   * a new array of objects by spreading each event schedule detail.
   *
   * @returns {Array<Object>} An array of events for the event schedule. If `details` is not available, returns an empty array.
   */
  schedules = computed(() => {
    const details = this.details.data();
    if (details) {
      return details.detailsForEventSchedule.map((e) => ({ ...e }));
    }

    return [];
  });

  /**
   * A computed property that retrieves the `eventPrototypeUuid` from the tournament details.
   *
   * @returns {string} The UUID of the event prototype.
   */
  eventPrototypeUuid = computed(() => {
    return this.details.data()?.eventPrototypeUuid || '';
  });

  /**
   * A computed property that returns an array of event schedule UUIDs.
   * It maps over the schedules and extracts the `eventScheduleUuid` from each schedule.
   *
   * @returns {string[]} An array of event schedule UUIDs.
   */
  schedulesIds = computed<SchedulesRecordItem[]>(() => {
    const prototypeId = this.eventPrototypeUuid();
    return this.schedules().map((e) => ({
      eventPrototypeUuid: prototypeId!,
      eventScheduleUuid: e.eventScheduleUuid,
    }));
  });

  /**
   * Generates a query to fetch the next events to play now based on the provided prototype ID and schedule IDs.
   *
   * @property {string} prototypeId - The UUID of the event prototype.
   * @property {string[]} scheduleIds - The array of schedule IDs.
   */
  toPlayNowQuery = multiNextEventsQuery(this.schedulesIds, {
    key: this.eventPrototypeUuid,
  });

  /**
   * Computes the events based on the schedules and the data from the `toPlayNowQuery`.
   *
   * @returns An array of schedules, each potentially augmented with a `toPlayNow` property.
   *  If there is a corresponding entry in `toPlayNow` for a schedule, the `toPlayNow`
   *  property will be set to the first element of that entry (if it exists), otherwise
   *  it will be set to `null`. If there is no corresponding entry in `toPlayNow`, the
   *  schedule will be returned as is.
   */
  events = computed(() => {
    const details = this.details.data();
    const eventPrototypeUuid = this.eventPrototypeUuid();
    const schedules = this.schedules();
    const toPlayNow = this.toPlayNowQuery.data();

    if (schedules.length && toPlayNow && eventPrototypeUuid) {
      const defaultPlayNow = {
        eventName: details?.name,
      } as unknown as NextEvent;
      return schedules.map((schedule) => {
        const eventsPrototypeNextEvents = toPlayNow[eventPrototypeUuid];
        if (
          eventsPrototypeNextEvents &&
          eventsPrototypeNextEvents[schedule.eventScheduleUuid]
        ) {
          const nextEventsFroScheduler =
            eventsPrototypeNextEvents[schedule.eventScheduleUuid];
          return {
            ...schedule,
            toPlayNow:
              nextEventsFroScheduler.length > 0
                ? nextEventsFroScheduler[0]
                : defaultPlayNow,
          };
        }

        return { ...schedule, toPlayNow: defaultPlayNow };
      });
    }

    return schedules;
  });

  /**
   * Computes an array of information objects based on the tournament details.
   * Each object contains a key, an optional value, and an optional icon.
   *
   * The information includes:
   * - Description: The description of the game or a default value 'Arcade, Action'.
   * - Experience: The experience type, either 'Casual' or 'Life Experience'.
   * - Browser: The browser metadata.
   * - System: The system metadata.
   * - Format: The format metadata, if available.
   *
   * @returns An array of objects containing key, value, and icon properties.
   */
  information = computed(() => {
    const details = this.details.data();
    const data: { key: string; value?: string; icon?: string }[] = [];

    if (details) {
      data.push({
        key: 'Description',
        value: details?.game?.description || 'Arcade, Action',
        icon: 'notes.svg',
      });

      data.push({
        key: 'Experience',
        value: getExperience(details),
        icon: 'star.svg',
      });

      data.push({
        key: 'Browser',
        value: details?.metadata?.browser,
        icon: 'language.svg',
      });

      data.push({
        key: 'System',
        value: details?.metadata?.system,
        icon: 'devices.svg',
      });

      if (details?.metadata?.format) {
        data.push({
          key: 'Format',
          value: details?.metadata?.format,
          icon: 'format.svg',
        });
      }
    }

    return data;
  });

  enabledFilters = computed(() => this.events().length >= 5);
  showFilterPanel = signal<boolean>(false);
  isAuthenticated = toSignal(this.#authFacade.isLoggedIn$);

  constructor() {
    effect(() => {
      const details = this.details.data();
      const events = this.toPlayNowQuery.data();
      const error = this.details.error();

      if (error) {
        this.#router.navigate(['/'], { relativeTo: this.#route.parent });
        return;
      }

      if (details) {
        const game = details.game;

        // update page title
        this.#title.setTitle(`${this.tournamentType} - ${game.name}`);

        // update breadcrumbs with tournament name
        this.#breadcrumbService.updateLabel('tournamentName', details?.name);
      }

      if (events) {
        Object.keys(events).forEach((eventPrototypeUuid) => {
          Object.keys(events[eventPrototypeUuid]).forEach(
            (eventScheduleUuid) => {
              this.#queryClient.setQueryData(
                ['next-events', eventPrototypeUuid, eventScheduleUuid],
                events[eventPrototypeUuid][eventScheduleUuid],
              );
            },
          );
        });
      }
    });
  }

  ngOnInit(): void {
    let tournamentId = '';
    this.intPageView();
    this.#route.params
      .pipe(untilDestroyed(this))
      .subscribe((params: Params) => {
        tournamentId = params.tournamentId + '';
        this.tournamentType = this.#route.snapshot.data.tournamentType;
        const queryParams = this.#route.snapshot.root.queryParams;
        if (queryParams) {
          this.view = queryParams.view || 'tournaments';
        }
        this.tabsLinks.map((t) => {
          t.path = `/${this.tournamentType}/${tournamentId}`;

          if (this.view === t.view) {
            t.active = true;
          }
          return t;
        });
      });
  }

  intPageView() {
    this.view$.subscribe((params) => {
      this.view = params.view || 'tournaments';
    });
  }

  getIFrameUrl(data: any) {
    const parsedUrl = data.assetsUrl.replace('index.html', 'prefetch.html');
    const url =
      parsedUrl +
      '?playInstanceUuid=&eventSeed=0&gameKey=&transient=true&returnToUrl=&v=' +
      new Date().getTime();
    this.freeGameUrl =
      this.#sanitizer.bypassSecurityTrustResourceUrl(url) || '';
  }

  openErrorDialog(errorType: string, openTournament?: CasualTournamentDetails) {
    this.#dialog.open(ModalAlertComponent, {
      data: {
        modalType: errorType,
        currentUser: this.currentUser(),
        tournament: openTournament,
      },
    });
  }

  async onTryForFree() {
    if (this.details.data()) {
      const isBlocked = this.#blockDeviceService.isBlocked(
        this.details.data()?.game.blockedDevices,
      );
      if (isBlocked) {
        this.openErrorDialog('game-blocked');
        return;
      }

      const url = this.#matchMakingService.getFreeGameUrl(this.details.data()!);
      try {
        await this.sendGAEvent();
      } catch (error) {
        console.error(error);
      }
      window.location.href = url;
    }
  }

  onLogin() {
    window.location.href = '/auth/sign-in?redirectUrl=' + window.location.href;
  }

  private async sendGAEvent(): Promise<any> {
    if (this.details.data() && this.currentUser()) {
      const dateTime = Date.now();
      const event = {
        event: GAEventKeys.USER_TRY_FOR_FREE,
        timestamp: dateTime,
        tournament_id: 'user_try_for_free',
        type_of_tournament: 'casual',
        total_prize: 0,
        game: this.details.data()?.game.name,
        user_name: this.currentUser()!.user.username,
        tournament_name: this.details.data()?.name,
        user_id: this.currentUser()!.user.uuid,
        number_of_player: 1,
      };
      return this.#gtmService.pushTag(event);
    }
  }

  onShare(schedule?: EventSchedule) {
    // build share url
    if (this.details.data()) {
      let urlStr = schedule
        ? `${window.location.href}/${schedule.eventScheduleUuid}`
        : window.location.href;

      if (
        this.details.data()?.lifeExperience &&
        this.details.data()?.metadata?.vip === 'true'
      ) {
        urlStr = `${window.location.origin}/vip/${this.details.data()?.url}`;
      }
      const url = new URL(urlStr);
      this.#shareService.share({
        title: 'FantaZ',
        text: 'Play Casual tournaments',
        url: url.toString(),
        currentUser: this.currentUser(),
      });
    }
  }
  onView(schedule: EventSchedule) {
    const params = new URLSearchParams(window.location.search);
    const inviteBy = params.get('inviteBy') || '';
    const queryParams: { inviteBy?: string } = {};
    if (inviteBy) {
      queryParams.inviteBy = inviteBy;
    }
    this.#router.navigate([`${schedule.eventScheduleUuid}`], {
      relativeTo: this.#route,
      queryParams,
    });
  }

  onFilterChange(filters: RoomFilterModel[]) {}

  isInRange(value: number, item: RoomFilter) {
    if (item.type === 'less' && value < item.value.start) {
      return true;
    }

    if (item.type === 'more' && value >= item.value.start) {
      return true;
    }

    if (item.type === 'exact' && value == item.value.start) {
      return true;
    }

    return value >= item.value.start && value <= item.value.end;
  }

  onShowFilter(show: boolean) {
    this.showFilter = show;
  }

  onToggleFilter($event: boolean, history?: boolean) {
    this.showFilterPanel.set($event);
  }
}
